import { useState } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarRange, FileText, Layers, Pencil, PenLine, BadgeCheck } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// One completed day: charge codes + Day Profitability. Shared by Waiting to Invoice & Weekly Billing.
// onEditDay (optional) renders a per-day Edit button — passed only where per-day editing is wanted.
export function DayCard({ day, jobId, index, onEditDay }) {
  const [busy, setBusy] = useState(false);
  const [excluded, setExcluded] = useState(!!day.cashflow_excluded);
  const fin = day.financials || {};
  const lines = day.charge_lines || [];
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/jobs/${jobId}/dockets/${index}/docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${day.docket_number || "docket"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate docket PDF"); }
    finally { setBusy(false); }
  };
  const toggleCashflow = async () => {
    try {
      const r = await api.post(`/jobs/${jobId}/dockets/${index}/cashflow-toggle`);
      setExcluded(r.data.cashflow_excluded);
      toast.success(r.data.cashflow_excluded ? "Day excluded from Weekly Cashflow" : "Day included in Weekly Cashflow");
    } catch (e) { toast.error("Could not update cashflow inclusion"); }
  };
  return (
    <div data-testid={`day-card-${jobId}-${index}`} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center justify-between gap-3 bg-slate-50 px-4 py-2.5 border-b border-slate-200">
        <div className="min-w-0">
          <p className="font-semibold text-navy text-sm flex items-center gap-2">
            <CalendarRange className="w-4 h-4 text-slate-400" />{formatDate(day.date)}
            <span className="text-slate-400 font-normal">· {day.docket_number}</span>
          </p>
          {day.technician_name && <p className="text-xs text-slate-500 mt-0.5">{day.technician_name}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(day.auto_approved || day.client_signed) && (
            <span data-testid={`day-approved-${jobId}-${index}`} title={day.auto_approved ? "Auto-approved: no client response within 5 days" : "Signed by the client"}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50">
              <BadgeCheck className="w-3.5 h-3.5" />{day.auto_approved ? "Approved (auto)" : "Approved"}
            </span>
          )}
          {onEditDay && (
            <Button size="sm" variant="outline" data-testid={`day-edit-${jobId}-${index}`} onClick={() => onEditDay(day)}
              className="bg-white border-amber-300 text-amber-700 hover:bg-amber-50">
              <Pencil className="w-4 h-4 mr-1.5" />Edit
            </Button>
          )}
          <button data-testid={`day-cashflow-${jobId}-${index}`} onClick={toggleCashflow}
            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border ${excluded ? "border-slate-200 text-slate-400 bg-white" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
            {excluded ? "Excluded from cashflow" : "In cashflow"}
          </button>
          <Button size="sm" variant="outline" data-testid={`day-docket-${jobId}-${index}`} onClick={download} disabled={busy} className="bg-white">
            <FileText className="w-4 h-4 mr-1.5" />{busy ? "Preparing…" : "Docket PDF"}
          </Button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {day.description_of_works && (
          <div data-testid={`day-desc-${jobId}-${index}`} className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5">Description of Works</p>
            <p className="text-sm text-slate-700 whitespace-pre-line">{day.description_of_works}</p>
          </div>
        )}
        {lines.length > 0 ? (
          <div className="text-sm">
            {lines.map((l, i) => (
              <div key={i} className="flex justify-between gap-3 py-1 border-b border-slate-50 last:border-0">
                <span className="text-slate-600 truncate">{l.description} <span className="text-slate-400">× {l.qty}{l.unit ? ` ${l.unit}` : ""}</span></span>
                <span className="text-navy font-medium shrink-0">{money(l.amount)}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400">No charge codes recorded on this day's docket.</p>}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between bg-slate-50 px-3 py-1.5 border-b border-slate-200">
            <span className="text-xs font-semibold text-navy">Day Profitability</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Office Only</span>
          </div>
          <div className="p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Revenue (ex GST)</span><span className="text-navy">{money(fin.revenue)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Labour {fin.labour_basis === "metre" ? `(${fin.metres}m @ metre rate)` : `(${fin.hours || 0}h${fin.is_saturday ? " · Sat rate" : ""})`}</span><span className="text-red-500">{fin.labour_cost > 0 ? `-${money(fin.labour_cost)}` : money(0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="text-red-500">{fin.material_cost > 0 ? `-${money(fin.material_cost)}` : money(0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Overhead</span><span className="text-red-500">{fin.overhead_cost > 0 ? `-${money(fin.overhead_cost)}` : money(0)}</span></div>
            <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1"><span className="text-navy">Gross Operating Margin</span><span data-testid={`day-profit-${jobId}-${index}`} className={fin.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{money(fin.gross_profit)} ({fin.margin || 0}%)</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Full per-day profit review: each day + a combined total. Identical in Waiting to Invoice & Weekly Billing.
export default function DayProfitBreakdown({ days = [], daysTotal, jobId, pendingDays = 0, pendingDates = [], heading = "Completed days — review each day's profit", onEditDay }) {
  return (
    <div data-testid={`day-profit-breakdown-${jobId}`} className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{heading}</p>
      {days.length === 0 && <p className="text-sm text-slate-400">No completed days yet.</p>}
      {days.map((day) => (
        <DayCard key={day.index} day={day} jobId={jobId} index={day.index} onEditDay={onEditDay} />
      ))}
      {days.length > 0 && daysTotal && (
        <div data-testid={`day-total-${jobId}`} className="border-2 border-navy/20 rounded-xl overflow-hidden bg-white">
          <div className="flex items-center justify-between bg-navy px-4 py-2.5">
            <span className="text-sm font-bold text-white flex items-center gap-2"><Layers className="w-4 h-4" />Combined Total — all {days.length} days</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">Office Only</span>
          </div>
          <div className="p-4 space-y-1.5 text-sm">
            {(daysTotal.line_items || []).length > 0 && (
              <div className="pb-2 mb-2 border-b border-slate-100 space-y-1">
                {(daysTotal.line_items || []).map((l, i) => (
                  <div key={i} className="flex justify-between gap-3">
                    <span className="text-slate-500 truncate">{formatDate(l.date)} · {l.description} <span className="text-slate-400">× {l.qty}{l.unit ? ` ${l.unit}` : ""}</span></span>
                    <span className="text-navy shrink-0">{money(l.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">Revenue (ex GST)</span><span className="text-navy font-medium">{money(daysTotal.revenue)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST (10%)</span><span className="text-slate-500">{money(daysTotal.gst)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600 font-semibold">Total (inc GST)</span><span className="text-navy font-bold">{money(daysTotal.total_inc_gst)}</span></div>
            <div className="flex justify-between pt-2 mt-1 border-t border-slate-100"><span className="text-slate-500">Labour ({daysTotal.hours || 0}h)</span><span className="text-red-500">{daysTotal.labour_cost > 0 ? `-${money(daysTotal.labour_cost)}` : money(0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="text-red-500">{daysTotal.material_cost > 0 ? `-${money(daysTotal.material_cost)}` : money(0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Overhead</span><span className="text-red-500">{daysTotal.overhead_cost > 0 ? `-${money(daysTotal.overhead_cost)}` : money(0)}</span></div>
            <div className="flex justify-between font-bold border-t border-slate-200 pt-1.5 mt-1 text-base"><span className="text-navy">Combined Gross Operating Margin</span><span data-testid={`day-total-profit-${jobId}`} className={daysTotal.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{money(daysTotal.gross_profit)} ({daysTotal.margin || 0}%)</span></div>
          </div>
        </div>
      )}
      {pendingDays > 0 && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <CalendarRange className="w-4 h-4" />Waiting on {pendingDays} more day{pendingDays > 1 ? "s" : ""} before this job can be invoiced ({(pendingDates || []).map((d) => formatDate(d)).join(", ")}).
        </p>
      )}
    </div>
  );
}

// Days still with the client to sign — shown in Waiting to Invoice, locked until signed or auto-approved.
export function AwaitingSignatureBanner({ days = [], jobId }) {
  if (!days.length) return null;
  return (
    <div className="px-4 pb-3 space-y-1.5" data-testid={`wti-awaiting-signature-${jobId}`}>
      {days.map((d, i) => (
        <p key={i} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid={`wti-awaiting-signature-${jobId}-${d.date}`}>
          <PenLine className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>With client to sign</b> · {formatDate(d.date)}{d.worker ? ` · ${d.worker}` : ""} · sent {formatDate((d.sent_at || "").slice(0, 10))}
            {d.customer_email ? ` to ${d.customer_email}` : ""} · auto-approves {formatDate((d.auto_approve_at || "").slice(0, 10))}. Locked until signed or approved.</span>
        </p>
      ))}
    </div>
  );
}
